// src/pages/BIViewer.tsx — Редизайн 2026 (стабильный + новый PDF)
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Download, LayoutPanelLeft, LayoutPanelTop, X,
  BarChart3, Sparkles, FileDown,
} from 'lucide-react';
import { getDashboard, updateDashboard, BIDashboard, BIWidget } from '../api/bi';
import BILayout from '../components/BILayout';
import GridLayout, { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { snapdom } from '@zumer/snapdom';
import { jsPDF } from 'jspdf';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, FunnelChart, Funnel,
  RadialBarChart, RadialBar, Treemap, ScatterChart, Scatter, LabelList
} from 'recharts';

const COLORS = [
  'rgba(106, 17, 203, 0.8)', 'rgba(37, 117, 252, 0.8)', 'rgba(40, 167, 69, 0.8)',
  'rgba(255, 77, 79, 0.8)', 'rgba(250, 173, 20, 0.8)', 'rgba(155, 89, 182, 0.8)',
  'rgba(52, 152, 219, 0.8)'
];

const BIViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const widgetAreaRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dashboard, setDashboard] = useState<BIDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState<Layout[]>([]);
  const [exporting, setExporting] = useState(false);
  const [showOrientationSelect, setShowOrientationSelect] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);

  useEffect(() => {
    if (id) loadDashboard();
  }, [id]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) setGridWidth(containerRef.current.clientWidth);
    };
    updateWidth();
    // Один resize через RAF, без 10-кратного цикла
    const t = setTimeout(updateWidth, 200);
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateWidth);
    };
  }, [dashboard]);

  const loadDashboard = async () => {
    try {
      const res = await getDashboard(id!);
      setDashboard(res.data);
      const savedLayout: Layout[] = res.data.widgets.map((w: BIWidget) => ({
        i: w.id,
        x: w.layout?.x || 0,
        y: w.layout?.y || 0,
        w: w.layout?.w || (w.size === 'small' ? 2 : w.size === 'medium' ? 3 : 4),
        h: w.layout?.h || 2,
        minW: 1, minH: 1, maxW: 6, maxH: 4,
      }));
      setLayout(savedLayout);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onLayoutChange = (newLayout: Layout[]) => {
    setLayout(newLayout);
    if (dashboard) {
      const updatedWidgets = dashboard.widgets.map(w => {
        const item = newLayout.find(l => l.i === w.id);
        if (item) w.layout = { x: item.x, y: item.y, w: item.w, h: item.h };
        return w;
      });
      updateDashboard(dashboard._id, { ...dashboard, widgets: updatedWidgets }).catch(console.error);
    }
  };

  const renderWidget = (widget: BIWidget) => {
    const data = widget.userData || [];
    if (data.length === 0) return <div style={styles.noData}>Нет данных</div>;

    const commonProps = {
      width: '100%', height: '100%', minHeight: 200,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };
    const tooltipStyle = {
      backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(8px)',
      border: '1px solid rgba(102,126,234,0.2)', borderRadius: '12px',
      boxShadow: '0 8px 20px rgba(31,38,135,0.1)', color: '#1a1a2e', fontSize: 12,
    };
    const axisColor = '#8b8fa8';
    const gridColor = 'rgba(102,126,234,0.08)';
    const labelColor = '#1a1a2e';
    const animDuration = 0;

    switch (widget.type) {
      case 'kpi':
        return (
          <div style={styles.kpiWrapper}>
            <div style={styles.kpiValue}>
              {data[0]?.value !== undefined ? data[0].value : data.length}
            </div>
          </div>
        );
      case 'bar':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={data}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke={axisColor} tick={{ fontSize: 12 }} />
              <YAxis stroke={axisColor} tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="url(#barGradient)" radius={[8, 8, 0, 0]} barSize={40} animationDuration={animDuration}>
                <LabelList dataKey="value" position="top" fill={labelColor} fontSize={12} />
              </Bar>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(37, 117, 252, 0.9)" />
                  <stop offset="100%" stopColor="rgba(37, 117, 252, 0.4)" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'stackedBar':
        const stackedData = data.map(d => ({
          name: d.name,
          value1: d.value1 !== undefined ? d.value1 : (d.value !== undefined ? d.value : 0),
          value2: d.value2 !== undefined ? d.value2 : 0,
        }));
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={stackedData}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke={axisColor} />
              <YAxis stroke={axisColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value1" stackId="a" fill="rgba(106, 17, 203, 0.8)" radius={[8, 8, 0, 0]} animationDuration={animDuration} />
              <Bar dataKey="value2" stackId="a" fill="rgba(37, 117, 252, 0.8)" radius={[8, 8, 0, 0]} animationDuration={animDuration} />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer {...commonProps}>
            <LineChart data={data}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke={axisColor} />
              <YAxis stroke={axisColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="value" stroke="rgba(106, 17, 203, 0.9)" strokeWidth={3} dot={{ r: 4, fill: '#6a11cb' }} activeDot={{ r: 6 }} animationDuration={animDuration}>
                <LabelList dataKey="value" position="top" fill={labelColor} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer {...commonProps}>
            <AreaChart data={data}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke={axisColor} />
              <YAxis stroke={axisColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="value" stroke="rgba(136, 132, 216, 0.9)" fill="url(#areaGradient)" animationDuration={animDuration}>
                <LabelList dataKey="value" position="top" fill={labelColor} />
              </Area>
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(136, 132, 216, 0.8)" />
                  <stop offset="100%" stopColor="rgba(136, 132, 216, 0.1)" />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer {...commonProps}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label animationDuration={animDuration}>
                {data.map((_, idx) => (
                  <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'funnel':
        return (
          <ResponsiveContainer {...commonProps}>
            <FunnelChart>
              <Tooltip contentStyle={tooltipStyle} />
              <Funnel data={data} dataKey="value" nameKey="name" animationDuration={animDuration}>
                <LabelList position="right" fill={labelColor} />
                {data.map((_, idx) => <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />)}
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        );
      case 'radial':
        return (
          <ResponsiveContainer {...commonProps}>
            <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="80%" data={data}>
              <RadialBar dataKey="value" fill="url(#radialGradient)" background={{ fill: 'rgba(102,126,234,0.1)' }} animationDuration={animDuration} />
              <Tooltip contentStyle={tooltipStyle} />
              <defs>
                <linearGradient id="radialGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(37, 117, 252, 0.9)" />
                  <stop offset="100%" stopColor="rgba(106, 17, 203, 0.9)" />
                </linearGradient>
              </defs>
            </RadialBarChart>
          </ResponsiveContainer>
        );
      case 'treemap':
        return (
          <ResponsiveContainer {...commonProps}>
            <Treemap data={data} dataKey="value" stroke="#fff" fill="rgba(106, 17, 203, 0.8)" animationDuration={animDuration} />
          </ResponsiveContainer>
        );
      case 'scatter':
        const scatterData = data.map(d => ({
          x: d.x !== undefined ? d.x : (d.value !== undefined ? d.value : 0),
          y: d.y !== undefined ? d.y : (d.value !== undefined ? d.value : 0),
          name: d.name,
        }));
        return (
          <ResponsiveContainer {...commonProps}>
            <ScatterChart>
              <CartesianGrid stroke={gridColor} />
              <XAxis dataKey="x" stroke={axisColor} />
              <YAxis dataKey="y" stroke={axisColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Scatter data={scatterData} fill="rgba(106, 17, 203, 0.8)" animationDuration={animDuration} />
            </ScatterChart>
          </ResponsiveContainer>
        );
      case 'gauge':
        const value = data[0]?.value !== undefined ? data[0].value : 0;
        const maxValue = 100;
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: 800, color: '#2575fc' }}>{value}</div>
            <div style={{ width: '80%', height: '12px', background: 'rgba(102,126,234,0.15)', borderRadius: '6px', marginTop: '10px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min((value / maxValue) * 100, 100)}%`, height: '12px', background: 'linear-gradient(90deg, #6a11cb, #2575fc)', borderRadius: '6px' }} />
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#8b8fa8' }}>max: {maxValue}</div>
          </div>
        );
      case 'heatmap':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={data}>
              <CartesianGrid stroke={gridColor} />
              <XAxis dataKey="name" stroke={axisColor} />
              <YAxis stroke={axisColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="rgba(255, 124, 67, 0.8)" radius={[8, 8, 0, 0]} animationDuration={animDuration}>
                <LabelList dataKey="value" position="top" fill={labelColor} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'table':
        return (
          <div style={{ overflowX: 'auto', maxHeight: 300, borderRadius: '12px' }}>
            <table style={{ ...styles.table, color: '#1a1a2e' }}>
              <thead>
                <tr><th>Название</th><th>Значение</th></tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td>{row.name || '-'}</td>
                    <td>{row.value !== undefined ? row.value : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return null;
    }
  };

  // ─────────── PDF EXPORT (полностью переписан) ───────────
  const exportToPDF = async (orientation: 'portrait' | 'landscape') => {
    if (!widgetAreaRef.current || !dashboard) return;
    setExporting(true);

    // Скрываем кнопки управления и hero перед снимком
    const heroEl = document.getElementById('bi-viewer-hero');
    const prevHeroDisplay = heroEl?.style.display;
    if (heroEl) heroEl.style.display = 'none';

    // Растягиваем контейнер чтобы влезли все виджеты
    const originalWidth = widgetAreaRef.current.style.width;
    const originalHeight = widgetAreaRef.current.style.height;
    widgetAreaRef.current.style.width = '1400px';   // фиксированная ширина для PDF
    widgetAreaRef.current.style.height = 'auto';

    // Форсим пересчёт grid
    window.dispatchEvent(new Event('resize'));
    await new Promise(resolve => setTimeout(resolve, 400));

    try {
      const result = await snapdom(widgetAreaRef.current, {
        scale: 2,
        backgroundColor: '#f8fafd',
        embedFonts: true,
      });
      const img = await result.toPng();

      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: 'a4',
      });

      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const margin = 10;

      // Приводим картинку к формату A4 с сохранением пропорций
      const ratio = img.width / img.height;
      const availW = pdfW - margin * 2;
      const availH = pdfH - margin * 2;
      let drawW = availW;
      let drawH = drawW / ratio;
      if (drawH > availH) {
        drawH = availH;
        drawW = drawH * ratio;
      }
      const x = (pdfW - drawW) / 2;
      const y = margin;

      // Одна картинка → одна страница (масштабируем)
      pdf.addImage(img.src, 'PNG', x, y, drawW, drawH);
      pdf.save(`${dashboard.name || 'dashboard'}_${orientation}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Не удалось создать PDF. Попробуйте ещё раз.');
    } finally {
      if (heroEl && prevHeroDisplay !== undefined) heroEl.style.display = prevHeroDisplay;
      if (widgetAreaRef.current) {
        widgetAreaRef.current.style.width = originalWidth;
        widgetAreaRef.current.style.height = originalHeight;
      }
      window.dispatchEvent(new Event('resize'));
      setExporting(false);
      setShowOrientationSelect(false);
    }
  };

  if (loading) return <BILayout><div style={styles.center}>Загрузка...</div></BILayout>;
  if (!dashboard) return <BILayout><div style={styles.center}>Дашборд не найден</div></BILayout>;

  return (
    <BILayout>
      <div ref={dashboardRef} style={styles.root}>
        {/* ─── HERO ─── */}
        <div id="bi-viewer-hero" style={styles.hero}>
          <div style={styles.heroLeft}>
            <div style={styles.heroIconWrap}>
              <div style={styles.heroIconBg} />
              <div style={styles.heroIconContent}>
                <BarChart3 size={28} color="#fff" strokeWidth={2} />
              </div>
            </div>
            <div>
              <h1 style={styles.title}>{dashboard.name}</h1>
              <div style={styles.metaRow}>
                <div style={styles.metaChip}>
                  <Sparkles size={12} color="#667eea" />
                  <span>{dashboard.widgets.length} виджетов</span>
                </div>
                {dashboard.description && (
                  <span style={styles.metaDesc}>{dashboard.description}</span>
                )}
              </div>
            </div>
          </div>

          <div style={styles.actions}>
            <button
              onClick={() => navigate(`/app/bi/builder/${dashboard._id}`)}
              style={styles.editBtn}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Edit size={16} /> <span>Редактировать</span>
            </button>

            {showOrientationSelect ? (
              <div style={styles.orientationGroup}>
                <button onClick={() => exportToPDF('portrait')} disabled={exporting} style={styles.orientationBtn} title="Книжная ориентация">
                  <LayoutPanelTop size={16} /> <span>Книжная</span>
                </button>
                <button onClick={() => exportToPDF('landscape')} disabled={exporting} style={styles.orientationBtn} title="Альбомная ориентация">
                  <LayoutPanelLeft size={16} /> <span>Альбомная</span>
                </button>
                <button onClick={() => setShowOrientationSelect(false)} disabled={exporting} style={styles.cancelBtn} title="Отмена">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowOrientationSelect(true)}
                disabled={exporting}
                style={styles.exportBtn}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <FileDown size={16} /> <span>{exporting ? 'Экспорт…' : 'Скачать PDF'}</span>
              </button>
            )}
          </div>
        </div>

        {/* ─── WIDGETS ─── */}
        <div ref={widgetAreaRef}>
          <div ref={containerRef} style={{ width: '100%' }}>
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={150}
              width={gridWidth}
              onLayoutChange={onLayoutChange}
              isDraggable={true}
              isResizable={true}
              margin={[16, 16]}
              containerPadding={[0, 0]}
              useCSSTransforms={true}
            >
              {dashboard.widgets.map(widget => (
                <div
                  key={widget.id}
                  style={{
                    ...styles.widgetCard,
                    backgroundColor: widget.cardColor || 'rgba(255, 255, 255, 0.75)',
                  }}
                >
                  <h3 style={styles.widgetTitle}>{widget.title}</h3>
                  <div style={{ flex: 1, minHeight: 0 }}>{renderWidget(widget)}</div>
                </div>
              ))}
            </GridLayout>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bi-fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .bi-viewer-widget { animation: bi-fadeUp 0.4s ease-out backwards; }
      `}</style>
    </BILayout>
  );
};

// ═══════════ СТИЛИ ═══════════
const styles: Record<string, React.CSSProperties> = {
  root: { maxWidth: 1600, margin: '0 auto', paddingBottom: 40 },

  // HERO
  hero: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 20, marginBottom: 24, flexWrap: 'wrap',
  },
  heroLeft: { display: 'flex', alignItems: 'center', gap: 20, flex: 1, minWidth: 300 },
  heroIconWrap: { position: 'relative', width: 64, height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroIconBg: { position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6a11cb, #2575fc, #00c6ff)', borderRadius: 20, boxShadow: '0 14px 32px rgba(102,126,234,0.32)' },
  heroIconContent: { position: 'relative', display: 'flex', alignItems: 'center', zIndex: 1 },
  title: { fontSize: 26, fontWeight: 800, margin: '0 0 6px', background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: -0.4, lineHeight: 1.15 },
  metaRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  metaChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.15)', borderRadius: 100, fontSize: 11, fontWeight: 700, color: '#667eea' },
  metaDesc: { fontSize: 13, color: '#5a5f7a', lineHeight: 1.4 },

  actions: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },

  editBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', border: '1px solid rgba(102,126,234,0.2)', color: '#2575fc', borderRadius: 14, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit' },
  exportBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(102,126,234,0.25)', transition: 'all 0.2s', fontFamily: 'inherit' },

  orientationGroup: { display: 'flex', gap: 8, alignItems: 'center' },
  orientationBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 16px', background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(102,126,234,0.2)', color: '#2575fc', borderRadius: 14, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit' },
  cancelBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, background: 'rgba(102,126,234,0.1)', border: '1px solid rgba(102,126,234,0.2)', color: '#5a5f7a', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit' },

  // WIDGET CARD
  widgetCard: {
    borderRadius: 22, padding: 20, height: '100%',
    display: 'flex', flexDirection: 'column',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    wordBreak: 'break-word',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow: '0 12px 32px rgba(31,38,135,0.08)',
  },
  widgetTitle: { fontSize: 16, fontWeight: 800, marginBottom: 14, flexShrink: 0, color: '#1a1a2e', letterSpacing: -0.3 },

  kpiWrapper: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 8, minHeight: 0 },
  kpiValue: { fontSize: 'clamp(32px, 8vw, 56px)', fontWeight: 800, textAlign: 'center', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.1 },

  noData: { textAlign: 'center', color: '#8b8fa8', padding: 40, fontSize: 13, fontWeight: 600 },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },

  center: { textAlign: 'center', padding: 60, fontSize: 16, color: '#5a5f7a', fontWeight: 600 },
};

export default BIViewer;
